import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Table2, ChevronRight, Database, PenLine } from "lucide-react";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { Pill } from "@/components/brand/Pill";
import { Card, SectionHead, Mantra, MantraAccent } from "@/components/brand/Card";
import {
  ReferenceDataTabs,
  ReferenceDataTabsContent,
  ReferenceDataTabsList,
  ReferenceDataTabsTrigger,
} from "@/components/admin/reference-data/ReferenceDataTabs";
import { AgentsTab } from "@/components/admin/reference-data/AgentsTab";
import { Toolbar, ToolbarSearch, ToolbarChip } from "@/components/brand/Toolbar";
import { useReferenceDataSources } from "@/hooks/useApi";
import type { ReferenceDataSource } from "@/types/reference-data";
import { formatRelative } from "@/lib/datetime";
import { sourceLabels } from "@/lib/reference-data-labels";

type CategoryFilter = "all" | "curation" | "sap_package";
type HubTab = "tables" | "agents";

/** curation → "authored in-house" glyph/tone; sap_package → SAP download tone. */
function categoryPill(category: string, t: (k: string) => string) {
  if (category === "sap_package") return { tone: "gold" as const, label: t("SAP") };
  return { tone: "navy" as const, label: t("Curation") };
}

export default function AdminReferenceData() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // A aba vive na URL: o link da aba Agentes abre direto nela.
  const [params, setParams] = useSearchParams();
  const tab: HubTab = params.get("tab") === "agents" ? "agents" : "tables";
  const setTab = (next: string) =>
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        if (next === "agents") merged.set("tab", "agents");
        else merged.delete("tab");
        return merged;
      },
      { replace: true },
    );
  const { data, isLoading } = useReferenceDataSources();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CategoryFilter>("all");

  const sources = useMemo(() => data?.sources ?? [], [data?.sources]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sources.filter((s) => {
      if (category !== "all" && s.category !== category) return false;
      if (!q) return true;
      // Searched against the *rendered* labels: matching the raw pt-BR columns
      // would make the search miss what the user can actually read on screen.
      const labels = sourceLabels(t, s);
      return (
        s.table_name.toLowerCase().includes(q) ||
        labels.displayName.toLowerCase().includes(q) ||
        labels.origin.toLowerCase().includes(q)
      );
    });
  }, [sources, query, category, t]);

  const curation = filtered.filter((s) => s.category === "curation");
  const sap = filtered.filter((s) => s.category === "sap_package");

  const curationCount = sources.filter((s) => s.category === "curation").length;
  const sapCount = sources.filter((s) => s.category === "sap_package").length;
  const totalRows = sources.reduce((acc, s) => acc + (s.active_row_count ?? 0), 0);
  const lastActivity = sources
    .map((s) => s.last_activity_at)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  const kpis = [
    { label: t("Sources"), value: (data?.total_count ?? 0).toLocaleString(), sub: `${curationCount} ${t("curated")} · ${sapCount} SAP` },
    { label: t("Active rows"), value: totalRows.toLocaleString(), gold: true },
    { label: t("Curation"), value: curationCount.toLocaleString(), sub: t("Authored in-house") },
    { label: t("Last activity"), value: formatRelative(lastActivity), sub: t("Any ingestion run") },
  ];

  const open = (s: ReferenceDataSource) =>
    navigate(`/admin/reference-data/${encodeURIComponent(s.table_name)}`);

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Table2 />}
        overline={t("ADMIN · REFERENCE DATA")}
        title={t("Reference Data")}
        meta={
          <>
            <HeroMetaItem k={t("Version model")} v={t("B2 · immutable + active pointer")} />
            <HeroMetaSep />
            <HeroMetaItem k={t("Sources")} v={String(data?.total_count ?? 0)} />
          </>
        }
        kpis={kpis}
      />

      <div className="p-8">
        <ReferenceDataTabs value={tab} onValueChange={setTab}>
          <ReferenceDataTabsList>
            <ReferenceDataTabsTrigger value="tables">{t("Tables")}</ReferenceDataTabsTrigger>
            <ReferenceDataTabsTrigger value="agents">{t("Agents")}</ReferenceDataTabsTrigger>
          </ReferenceDataTabsList>
          <ReferenceDataTabsContent value="tables" className="mt-5 space-y-7">
            <section>
              <SectionHead
                overline={t("INVENTORY")}
                title={t("All sources")}
                tag={`${sources.length} ${t("tables")}`}
                sub={t(
                  "Maintain the reference tables that drive the agents — without touching the database. Click a source to view rows, upload a new CSV/xlsx, promote a draft, or roll back. Everything is versioned: only promotion goes live.",
                )}
              />

              <Card>
                <Toolbar>
                  <ToolbarSearch
                    placeholder={t("Search source, table, origin…")}
                    value={query}
                    onChange={setQuery}
                  />
                  <ToolbarChip active={category === "all"} onClick={() => setCategory("all")}>
                    {t("All")}
                  </ToolbarChip>
                  <ToolbarChip active={category === "curation"} onClick={() => setCategory("curation")}>
                    {t("Curation")}
                  </ToolbarChip>
                  <ToolbarChip active={category === "sap_package"} onClick={() => setCategory("sap_package")}>
                    {t("SAP")}
                  </ToolbarChip>
                </Toolbar>

                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: "38%" }}>{t("Source / Tables")}</th>
                      <th>{t("Category")}</th>
                      <th>{t("Origin")}</th>
                      <th>{t("Active version")}</th>
                      <th>{t("Rows")}</th>
                      <th style={{ width: 28 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-[#7b8799] text-sm">
                          {t("No reference-data sources match your filters.")}
                        </td>
                      </tr>
                    ) : (
                      <>
                        {curation.length > 0 && (
                          <GroupHeader
                            icon={<PenLine className="w-3 h-3" />}
                            label={t("CURATION · AUTHORED IN-HOUSE")}
                          />
                        )}
                        {curation.map((s) => (
                          <SourceRow key={s.table_name} source={s} onOpen={() => open(s)} />
                        ))}
                        {sap.length > 0 && (
                          <GroupHeader
                            icon={<Database className="w-3 h-3" />}
                            label={t("SAP DOWNLOADS · UPLOAD & INGEST")}
                          />
                        )}
                        {sap.map((s) => (
                          <SourceRow key={s.table_name} source={s} onOpen={() => open(s)} />
                        ))}
                      </>
                    )}
                  </tbody>
                </table>

                <div className="px-4 py-3 flex justify-between items-center border-t border-[#f2f4f7] font-mono text-[11px] text-[#7b8799]">
                  <span>
                    {t("Showing {{shown}} of {{total}}", {
                      shown: filtered.length.toLocaleString(),
                      total: (data?.total_count ?? 0).toLocaleString(),
                    })}
                  </span>
                  <span>{t("Data is global · gated by tenant admin")}</span>
                </div>
              </Card>
            </section>

            <Mantra>
              {t("EDIT & INGEST CONVERGE")} · <MantraAccent>{t("DRAFT → PROMOTE")}</MantraAccent> ·{" "}
              {t("B2 VERSIONED")}
            </Mantra>
          </ReferenceDataTabsContent>
          <ReferenceDataTabsContent value="agents" className="mt-5">
            <AgentsTab />
          </ReferenceDataTabsContent>
        </ReferenceDataTabs>
      </div>
    </div>
  );
}

function GroupHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <tr>
      <td colSpan={6} className="!py-2.5 bg-[#fafbfc]">
        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-[#596b85] uppercase font-semibold">
          {icon}
          {label}
        </span>
      </td>
    </tr>
  );
}

function SourceRow({ source, onOpen }: { source: ReferenceDataSource; onOpen: () => void }) {
  const { t } = useTranslation();
  const cat = categoryPill(source.category, t);
  const labels = sourceLabels(t, source);
  return (
    <tr className="cursor-pointer hover:bg-[#fafbfc]" onClick={onOpen}>
      <td>
        <div className="flex flex-col gap-1">
          <span className="mono text-[13px] font-semibold text-[#08254c]">{source.table_name}</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[12px] text-[#596b85]">{labels.displayName}</span>
            {source.agents.map((a) => (
              <span
                key={a}
                className="font-mono text-[9px] uppercase tracking-[0.06em] text-[#7b8799] bg-[#f2f4f7] border border-[#e8ebf0] rounded px-1.5 py-px"
              >
                {a}
              </span>
            ))}
          </div>
        </div>
      </td>
      <td>
        <Pill tone={cat.tone}>{cat.label}</Pill>
      </td>
      <td className="muted text-[12px]">{labels.origin}</td>
      <td>
        {source.active_version ? (
          <span className="mono text-[13px] text-[#08254c]">{source.active_version}</span>
        ) : (
          <span className="mono muted text-[12px]">{t("none")}</span>
        )}
        {source.promoted_by && (
          <div className="mono muted text-[10px]">
            {t("by")} {source.promoted_by} · {formatRelative(source.promoted_at)}
          </div>
        )}
      </td>
      <td className="mono text-[13px] text-[#08254c]">{source.active_row_count.toLocaleString()}</td>
      <td>
        <ChevronRight className="w-4 h-4 text-[#b2bccb]" />
      </td>
    </tr>
  );
}

function SkeletonRow() {
  return (
    <tr>
      <td>
        <div className="space-y-1.5">
          <div className="h-3 w-40 rounded bg-[#f2f4f7]" />
          <div className="h-2.5 w-56 rounded bg-[#f7f7f7]" />
        </div>
      </td>
      <td>
        <div className="h-4 w-16 rounded-full bg-[#f2f4f7]" />
      </td>
      <td>
        <div className="h-3 w-28 rounded bg-[#f2f4f7]" />
      </td>
      <td>
        <div className="h-3 w-16 rounded bg-[#f2f4f7]" />
      </td>
      <td>
        <div className="h-3 w-10 rounded bg-[#f2f4f7]" />
      </td>
      <td />
    </tr>
  );
}
